import { endsWith, isString, startsWith } from 'lodash-es';

import {
  KEY_ACTION_NAME,
  OPERATOR_CONTAINS,
  OPERATOR_EMPTY,
  OPERATOR_ENDS,
  OPERATOR_EQ,
  OPERATOR_GT,
  OPERATOR_GTE,
  OPERATOR_IS_FALSE,
  OPERATOR_IS_TRUE,
  OPERATOR_LT,
  OPERATOR_LTE,
  OPERATOR_MATCH,
  OPERATOR_NEQ,
  OPERATOR_NOTCONTAINS,
  OPERATOR_NOTEMPTY,
  OPERATOR_NOTENDS,
  OPERATOR_NOTMATCH,
  OPERATOR_NOTSTARTS,
  OPERATOR_REGEXP,
  OPERATOR_STARTS,
  TARGET_ACTION,
  TARGET_DOM,
  TARGET_FALSE,
  TARGET_TRUE,
  TARGET_URL
} from './constants.mjs';

// convert from Java to JavaScript format
function safeTestRegExp(regexpString, string) {
  // replace all / with \/ except for \/
  const caseInsensitiveFlag = regexpString.includes('(?i)');

  // Remove the "(?i)" part and the slashes from the input string
  const pattern = regexpString.replace(/\(\?i\)|^\//g, '').replace(/\/$/g, '');

  // Create the RegExp object, making it case insensitive if necessary
  const flags = caseInsensitiveFlag ? 'i' : '';

  const regexp = new RegExp(pattern, flags);

  return regexp.test(String(string));
}

function matchRegexp(regexp, string) {
  return safeTestRegExp(regexp, string || '') ? string : undefined;
}

// copied from w.js src
function getUrlParam(param) {
  const params = {};

  window.location.href.replace(
    /[?&]+([^=&]+)=([^&]*)/gi,
    function (m, key, value) {
      params[key] = decodeURIComponent(value.split('+').join(' '));
    }
  );

  return params[param];
}

export function createQueryMatcher(match) {
  return function matchQuery(value) {
    if (!match?.length) return value;

    const [operator, ...matchValues] = isString(match)
      ? [OPERATOR_REGEXP, match]
      : match;

    for (let i = 0; i < matchValues.length; i++) {
      const matchValue = matchValues[i];

      switch (operator) {
        case OPERATOR_CONTAINS:
          if (String(value ?? '').includes(matchValue)) return value;
          break;

        case OPERATOR_EMPTY:
          if (!value) return value;
          break;

        case OPERATOR_ENDS:
          if (endsWith(value ?? '', matchValue)) return value;
          break;

        case OPERATOR_EQ:
          if (value === matchValue) return value;
          break;

        case OPERATOR_GT:
          if (value > matchValue) return value;
          break;

        case OPERATOR_GTE:
          if (value >= matchValue) return value;
          break;

        case OPERATOR_IS_FALSE:
          if (value === false) return value;
          break;

        case OPERATOR_IS_TRUE:
          if (value === true) return value;
          break;

        case OPERATOR_LT:
          if (value < matchValue) return value;
          break;

        case OPERATOR_LTE:
          if (value <= matchValue) return value;
          break;

        case OPERATOR_MATCH:
          if (value === matchValue) return value;
          break;

        case OPERATOR_NEQ:
          if (value !== matchValue) return value;
          break;

        case OPERATOR_NOTCONTAINS:
          if (!String(value ?? '').includes(matchValue)) return value;
          break;

        case OPERATOR_NOTEMPTY:
          if (value) return value;
          break;

        case OPERATOR_NOTENDS:
          if (!endsWith(value ?? '', matchValue)) return value;
          break;

        case OPERATOR_NOTMATCH:
          if (value !== matchValue) return value;
          break;

        case OPERATOR_NOTSTARTS:
          if (!startsWith(value ?? '', matchValue)) return value;
          break;

        case OPERATOR_REGEXP:
          if (matchRegexp(matchValue, value)) return value;
          break;

        case OPERATOR_STARTS:
          if (startsWith(value ?? '', matchValue)) return value;
          break;

        default:
          console.warn(`Unknown rule operator: ${operator}`); // eslint-disable-line no-console
          break;
      }
    }
  };
}

export function createQueryEvaluator(actionName, actionData, extendedData) {
  function queryEvaluator(target, args = {}, match) {
    switch (target) {
      case TARGET_ACTION: {
        const { property } = args;

        if (property === KEY_ACTION_NAME) return match(actionName);

        return match(extendedData[property]) || match(actionData[property]);
      }

      case TARGET_DOM: {
        const { target, attribute, selector, meta } = args;

        const element = document.querySelector(selector);

        if (!element) return undefined;

        if (target === 'content') {
          if (element.tagName === 'PASSWORD') {
            return undefined;
          }

          return match(element.textContent);
        } else if (target === 'attribute') {
          if (!attribute) return undefined;

          if (element.tagName === 'PASSWORD' && attribute === 'value') {
            return undefined;
          }

          return match(element.getAttribute(attribute));
        } else if (target === 'count') {
          return match(document.querySelectorAll(selector).length);
        }
      }

      case TARGET_URL: {
        const { property = 'href', param } = args;

        if (property === 'param' && param) return match(getUrlParam(param));

        return match(window.location[property]);
      }

      case TARGET_TRUE:
        return true;

      case TARGET_FALSE:
        return undefined;

      default:
        console.warn(`Unknown rule target: ${target}`); // eslint-disable-line no-console
        return undefined;
    }
    /* eslint-enable no-var */
  }

  return function evaluateQuery(query, path) {
    const [target, args, match] = query;

    const value = queryEvaluator(target, args, createQueryMatcher(match));

    return {
      match: Boolean(value),
      value,
      path
    };
  };
}

export function evaluateQuery(rule, queryEvaluator, path = []) {
  if (rule.query) {
    return queryEvaluator(rule.query, path);
  }

  if (rule.$and) {
    let result = {
      match: false,
      value: undefined,
      path: path.concat('$and')
    };

    for (let i = 0; i < rule.$and.length; i++) {
      result = evaluateQuery(
        rule.$and[i],
        queryEvaluator,
        path.concat(['$and', i])
      );

      if (!result.match) return result;
    }

    return result;
  }

  if (rule.$or) {
    let result = {
      match: false,
      value: undefined,
      path: path.concat('$or')
    };

    for (let i = 0; i < rule.$or.length; i++) {
      result = evaluateQuery(
        rule.$or[i],
        queryEvaluator,
        path.concat(['$or', i])
      );

      if (result.match) return result;
    }

    return result;
  }

  return {
    match: false,
    value: undefined,
    path
  };
}

function crawlQueryTree(rule, property, path) {
  const values = [];

  if (rule[property] !== undefined) values.push(rule[property]);

  if (path.length > 0) {
    values.push(
      ...crawlQueryTree(rule[path.slice(0, 1)], property, path.slice(1))
    );
  }

  return values;
}

export function evaluateQueryValue(query, queryEvaluator) {
  const result = evaluateQuery(query, queryEvaluator);

  const values = crawlQueryTree(
    query,
    result.match ? 'return' : 'default',
    result.path
  );

  if (result.value !== undefined) values.unshift(result.value);

  return values[values.length - 1];
}
